import React, { useContext, useEffect } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import HelpWorkRegistrationCard from '@Components/Cards/HelpWorkRegistrationCard'
import Button from '@Components/Buttons/Button'
import Pathes from '@App/dicts/pathes'
import ModalPortal from '@Components/Popups/ModalPortal'
import { openExternalHttp } from '@App/navigation/safe-navigation'
import Popup from '@Components/Popups/Popup'
import Navigation from '@App/navigation'
import { StoresContext } from '@App/contexts'
import BulkUploadErrorPopup from '@Components/Popups/BulkUploadErrorPopup'
import { BulkUploadErrorType } from '@App/enum/bulk-upload-error-type'

const FileUploadStep = () => {
    const { bulkUploadStore, popupStore, workStore, uiStore, rightsholderStore } = useContext(StoresContext)

    const checkValidationFileType = (fileName: string) => {
        const arrayName = fileName.split('.')
        const type = arrayName[arrayName.length - 1].toLowerCase()
        return type === 'xlsx' || type === 'csv'
    }

    const onClosePopup = () => {
        try {
            bulkUploadStore.setInfoModalForBulkUpload(null)
        } catch (error) {
            popupStore.setAlertFromError(error)
        }
    }

    const onLoadFile = (files: FileList | null) => {
        if (!files || !files[0]) {
            return
        }

        // clear old works
        bulkUploadStore.destroyAllWorksFromUpload()
        bulkUploadStore.destroyWorksOnCurrentPage()

        const isFileValid = checkValidationFileType(files[0].name)
        if (!isFileValid) {
            return
        }
        bulkUploadStore.setWorkFileDraftToLocal(files[0])
    }

    const loadTemplateFile = () => {
        openExternalHttp(Pathes.Upload.downloadTemplate)
    }

    useEffect(() => () => workStore.setWizardStepValidation(false), [])

    const modalErrorType = bulkUploadStore.modalErrorType
    const modalErrorMessage = bulkUploadStore.modalErrorMessage
    const openModal = modalErrorType !== undefined

    return (
        <>
            <div className="file-upload-step">
                <div className="file-upload-wrapper">
                    <div className="upload-area">
                        <p className="small">Please select the file you want to upload.</p>
                        <div className="file-area">
                            {bulkUploadStore.workFileDraftLocal ? (
                                <>
                                    <span>
                                        {bulkUploadStore.workFileDraftLocal
                                            ? bulkUploadStore.workFileDraftLocal.name
                                                ? bulkUploadStore.workFileDraftLocal.name
                                                : 'MLCBulkWork_V1.2.xlsx'
                                            : ''}
                                    </span>
                                    <Button
                                        mainColor
                                        type="primary-small"
                                        onClick={bulkUploadStore.destroyWorkFileDraftLocal}
                                    >
                                        Remove
                                    </Button>
                                </>
                            ) : null}
                            <input
                                formEncType="multipart/form-data"
                                type="file"
                                id="upload"
                                name="file"
                                accept=".xlsx,.csv"
                                onChange={(e) => {
                                    onLoadFile(e.target.files)
                                }}
                            />
                        </div>
                        <div className="label-button-wrapper">
                            <label htmlFor="upload" className="">
                                Browse Files
                            </label>
                        </div>
                        <p className="small">
                            <span className="bold-note">Please note:</span> Publishing information should only be
                            entered for shares you control. Please do not enter publishing and shares information for
                            publishers you do not represent.
                        </p>
                    </div>
                    <div className="publishers-help-area">
                        <HelpWorkRegistrationCard>
                            <h5 className="title">How do I format the file?</h5>
                            <p className="info">
                                To create a Bulk Work Registration file, first download the template provided below.
                                Once opened, fill out each column with as much information as you have. Definitions for
                                each column can be found under the Field Definitions tab.
                            </p>
                            <ul>
                                <li className="info">Required columns: Noted with an asterisk ( * ).</li>
                                <li className="info">
                                    Dependent fields: These fields are not required, but if you enter information in one
                                    column, you must enter information in the other. These columns are:
                                    <ul className="sub-list">
                                        <li className="info">Administrator Name and Administration IPI</li>
                                        <li className="info">Recording Title and Recording Artist Name</li>
                                        <li className="info">AKA Title and AKA Type Code</li>
                                    </ul>
                                </li>
                                <li className="info">
                                    Size: There is a maximum of 300 rows for upload. If your document requires more
                                    rows, you will need to create another document for upload.
                                </li>
                                <li className="info">Upload format: The file must save as a .xlsx format to upload.</li>
                            </ul>
                            <Button mainColor type="primary" onClick={loadTemplateFile}>
                                Download template
                            </Button>
                            <p className="version info">Current version: v1.2</p>
                        </HelpWorkRegistrationCard>
                    </div>
                </div>
            </div>

            <ModalPortal isOpen={openModal} onClose={onClosePopup}>
                <BulkUploadErrorPopup
                    modalErrorType={modalErrorType ?? BulkUploadErrorType.UNKNOWN}
                    modalErrorMessage={modalErrorMessage}
                    onClose={onClosePopup}
                />
            </ModalPortal>

            <ModalPortal isOpen={uiStore.isMobile} onClose={onClosePopup}>
                <Popup showClose={false} onClose={onClosePopup}>
                    <div className="info-head">
                        <p className="bold">
                            Bulk works registration is not available while on a mobile device. Please revisit the page
                            from a non-mobile device to continue with your bulk work registration. Click the back button
                            to return to the catalog page.
                        </p>
                    </div>

                    <div className="search-confirm-popup-content">
                        <div className="buttons-block">
                            <Button
                                mainColor
                                type="primary"
                                typeLink
                                linkTo={Navigation.rightholder.catalog.root(rightsholderStore.currentRightsholderId)}
                                onClick={onClosePopup}
                            >
                                Back
                            </Button>
                        </div>
                    </div>
                </Popup>
            </ModalPortal>
        </>
    )
}

export default observer(FileUploadStep)
